import { CookieKeys } from "../constants/cookieKeys";
import { getCookie } from "./getCookie";
import { setCookie } from "./setCookie";

/** Поднимаем версию, если состав категорий меняется — согласие спросим заново. */
export const COOKIE_CONSENT_VERSION = 1;

/** 180 дней */
const COOKIE_CONSENT_MAX_AGE = 15552000;

export type CookieConsentCategory =
  | "necessary"
  | "functional"
  | "analytics"
  | "marketing"
  | "thirdParty";

/** Категории, которые пользователь может отключить. */
export const OPTIONAL_COOKIE_CATEGORIES = [
  "functional",
  "analytics",
  "marketing",
  "thirdParty"
] as const satisfies readonly CookieConsentCategory[];

export type CookieConsent = Record<CookieConsentCategory, boolean> & {
  version: number;
};

const createConsent = (optional: boolean): CookieConsent => ({
  version: COOKIE_CONSENT_VERSION,
  necessary: true,
  functional: optional,
  analytics: optional,
  marketing: optional,
  thirdParty: optional
});

export const acceptAllConsent = () => createConsent(true);
export const rejectOptionalConsent = () => createConsent(false);

const isConsent = (value: unknown): value is CookieConsent => {
  if (typeof value !== "object" || value === null) {
    return false;
  }

  const consent = value as Record<string, unknown>;

  return (
    consent.version === COOKIE_CONSENT_VERSION &&
    OPTIONAL_COOKIE_CATEGORIES.every(
      (category) => typeof consent[category] === "boolean"
    )
  );
};

/**
 * Читается только в браузере: сервер про этот cookie ничего не знает,
 * поэтому страница остаётся статической и кэшируемой.
 */
export const readCookieConsent = (): CookieConsent | undefined => {
  const raw = getCookie(CookieKeys.CookieConsent);

  if (!raw) {
    return undefined;
  }

  try {
    const parsed: unknown = JSON.parse(raw);

    return isConsent(parsed) ? { ...parsed, necessary: true } : undefined;
  } catch {
    return undefined;
  }
};

export const writeCookieConsent = (consent: CookieConsent) => {
  setCookie(
    CookieKeys.CookieConsent,
    encodeURIComponent(JSON.stringify({ ...consent, necessary: true })),
    COOKIE_CONSENT_MAX_AGE
  );
};
